import { Alert, Badge, Button, Col, FormGroup, Input, Row } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import { CheckTreePicker, Whisper, Tooltip } from "rsuite";
import _ from "underscore";

import RequiredTitle from "../../elements/RequiredTitle";
import Divider from "../../elements/Divider";
import { Index } from "../../types/GlobalInterfaces";
import { Field, Module } from "../../types/TenantInterfaces";
import AppDrawer from "./AppDrawer";
import useCRUDIndex from "../../api/api-hooks/modules/useCRUDIndex";
import AppSpinner from "../../elements/AppSpinner";
import FieldUtils from "../../utils/FieldUtils";
import useFieldMap from "../../hooks/useFieldMap";

interface IndexDrawer {
  open: boolean;
  handleClose: () => void;
  module: Module;
  tenantID: string;
  isSupportal?: boolean;
  existingIndex?: Index | null;
}

interface CheckTreeItem {
  children?: CheckTreeItem[];
  label: string;
  refKey: string;
  value: string;
  fieldType: string;
  visible: boolean;
}

const ascending = "mdi mdi-sort-ascending";
const descending = "mdi mdi-sort-descending";
const arrayFieldTypes: string[] = [
  "Email",
  "Phone",
  "URL",
  "MultiReference",
  "MultiSelect",
  "Location",
  "MultiSubitem",
  "Tags",
];

const CreateEditIndexDrawer = ({
  open,
  handleClose,
  module,
  tenantID,
  isSupportal = true,
  existingIndex,
}: IndexDrawer) => {
  const [name, setName] = useState("");
  const [values, setValues] = useState<{ [key: string]: 1 | -1 | "2dsphere" }>(
    {}
  );
  const [error, setError] = useState("");
  const { crudIndexAction, crudLoadingIndexAction } = useCRUDIndex();
  const [arrayFieldAPIName, setArrayFieldAPIName] = useState("");
  const action = existingIndex ? "Update" : "Create";
  const fieldTypeMap = useFieldMap(
    module?.APIName,
    "FieldType",
    undefined,
    undefined,
    module
  );

  useEffect(() => {
    if (!existingIndex) return;

    setName(existingIndex.name);
    setValues(existingIndex.key);

    if (existingIndex.key) {
      for (let key in existingIndex.key) {
        const splitKey = key?.split(".");

        const fieldType = _.findWhere(module?.Fields, {
          APIName: splitKey[0],
        })?.FieldType;

        if (fieldType && arrayFieldTypes.includes(fieldType)) {
          setArrayFieldAPIName(key);
        }
      }
    }
  }, [existingIndex]);

  const fieldTreeData = useMemo(() => {
    if (!module?.Fields?.length) return { data: [], uncheckableData: [] };

    const treeData: CheckTreeItem[] = [];
    const uncheckableItems: string[] = [];

    for (const field of module?.Fields ?? []) {
      let subitemItems: any[] = [];
      const newItem: any = {
        label: field.Name,
        value: field.APIName,
        fieldType: field?.FieldType,
      };

      if (["id", "_RecordName", "_RemoteSystems"].includes(field?.APIName))
        continue;

      if (FieldUtils.fieldTypeIsSubitem(field.FieldType)) {
        uncheckableItems.push(field.APIName);
        subitemItems =
          field?.SubitemFields?.map((subField) => {
            return {
              label: `${field.Name}'s ${subField.Name}`,
              value: `${field.APIName}.${subField.APIName}`,
              fieldType: subField?.FieldType,
              parentType: field?.FieldType,
            };
          }) ?? [];
      }

      if (subitemItems?.length) {
        newItem.children = subitemItems;
      }

      treeData.push(newItem);
    }

    return {
      data: treeData,
      uncheckableData: uncheckableItems,
    };
  }, [module?.APIName, module?.Fields?.length]);

  const fieldAPINames = useMemo(() => {
    return _.keys(values);
  }, [values]);

  // To disbale checks.
  const disabledFieldsAPINames = useMemo(() => {
    if (arrayFieldAPIName) {
      return fieldTreeData.data.flatMap((field) => {
        if (Array.isArray(field.children) && field.children?.length) {
          return field.children
            .flatMap((cField) => {
              // if (field?.fieldType === "MultiSubitem") return [cField.value];

              if (
                (arrayFieldTypes.includes(cField?.fieldType) ||
                  field?.fieldType === "MultiSubitem") &&
                !fieldAPINames.includes(cField.value)
              )
                return [cField.value];

              return [];
            })
            .concat(!fieldAPINames.includes(field.value) ? [field.value] : []);
        }

        if (
          arrayFieldTypes.includes(field?.fieldType) &&
          !fieldAPINames.includes(field.value)
        )
          return [field.value];

        return [];
      });
    }

    if (fieldAPINames?.length < 10) return [];

    return fieldTreeData.data.flatMap((field) => {
      if (Array.isArray(field.children) && field.children?.length) {
        return field.children
          .flatMap((cField) => {
            if (!fieldAPINames.includes(cField.value)) return [cField.value];

            return [];
          })
          .concat(!fieldAPINames.includes(field.value) ? [field.value] : []);
      }

      if (!fieldAPINames.includes(field.value)) return [field.value];

      return [];
    });
  }, [fieldAPINames?.length, arrayFieldAPIName]);

  return (
    <AppDrawer
      open={open}
      onClose={() => {
        setValues({});
        setName("");
        setError("");
        handleClose();
      }}
      headerTitle={`${action} Index`}
      headerDescription={`${action} inside of ${module.Name} Module`}
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={onCRUDOperation}
          style={{ width: 80 }}
          disabled={
            crudLoadingIndexAction || !name || !Object.keys(values)?.length
          }
        >
          {crudLoadingIndexAction ? <AppSpinner size="sm" /> : action}
        </Button>
      }
      body={
        <>
          <FormGroup>
            <RequiredTitle title="Name" size="md" />
            <Input
              placeholder="Enter index name"
              value={name ?? ""}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </FormGroup>
          <FormGroup>
            <RequiredTitle title="Fields" size="md" />
            <CheckTreePicker
              data={fieldTreeData.data}
              placeholder="Select a Field"
              placement="bottomEnd"
              defaultExpandAll
              preventOverflow
              searchable={true}
              block
              disabledItemValues={disabledFieldsAPINames}
              value={fieldAPINames}
              onClean={onClean}
              onSelect={onSelect}
              // @ts-ignore
              onChange={onChange}
              cascade={true}
              uncheckableItemValues={fieldTreeData.uncheckableData}
            />
          </FormGroup>
          <Divider />
          <FormGroup>
            <h4 className="mb-1">Order By</h4>
            <div className="small mb-4">Select the order for each index</div>
            <Row className="text-dark font-weight-500 mt-2">
              <Col xs="6" className="">
                Field Name
              </Col>
              <Col xs="3">Order</Col>
            </Row>
            <Divider />
            {Object.keys(values).map((keyName) => {
              return (
                <Row className="font-weight-500 mt-2" key={keyName}>
                  <Col xs="6">{getFieldName(keyName)}</Col>
                  <Col xs="3">
                    {typeof values[keyName] === "string" ? (
                      <>
                        <Badge
                          color="secondary"
                          className="text-white"
                          style={{ fontSize: "12px" }}
                        >
                          {values[keyName]}
                        </Badge>
                      </>
                    ) : (
                      <>
                        {" "}
                        <Button
                          color="dark"
                          outline
                          size="sm"
                          className="btn-icon-only"
                          onClick={() => {
                            switchOrder(keyName);
                          }}
                        >
                          <Whisper
                            preventOverflow
                            placement="autoHorizontal"
                            trigger="hover"
                            speaker={
                              <Tooltip>
                                {values[keyName] === 1
                                  ? "Ascending (A-Z)"
                                  : "Descending (Z-A"}
                              </Tooltip>
                            }
                          >
                            <i
                              className={`mdi ${
                                values[keyName] === 1 ? ascending : descending
                              }`}
                            />
                          </Whisper>
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              );
            })}
          </FormGroup>
          {error && <Alert color="warning">{error}</Alert>}
        </>
      }
    />
  );

  function onSuccess() {
    setValues({});
    setName("");
    setError("");
    handleClose();
  }

  function onCRUDOperation() {
    crudIndexAction({
      action,
      moduleAPIName: module?.APIName,
      tenantID: tenantID,
      isSupportal,
      data: {
        name,
        ...(!!existingIndex &&
          name !== existingIndex.name && { oldName: existingIndex.name }),
        key: values,
      },
      onSuccess,
      onError: (error: string) => {
        setError(error);
      },
    });
  }

  function getFieldName(fieldAPIName: string) {
    let splitedField: string[] = [];
    if (fieldAPIName.includes(".")) {
      splitedField = fieldAPIName.split(".");
      fieldAPIName = splitedField[0];
    }

    const fieldSchema = _.findWhere(module.Fields, { APIName: fieldAPIName });

    if (!fieldSchema) return fieldAPIName;

    if (FieldUtils.fieldTypeIsSubitem(fieldSchema?.FieldType)) {
      const subitemField = _.findWhere(fieldSchema?.SubitemFields as Field[], {
        APIName: splitedField[1],
      });

      if (!subitemField) return fieldAPIName;

      return `${fieldSchema.Name}'s ${subitemField.Name}`;
    }

    return fieldSchema.Name;
  }

  function onChange(items: string[]) {
    items = items.slice(0, 10);

    const newValues = _.pick(values, items);

    if (_.keys(newValues).length === items?.length) {
      setValues({ ...newValues });
      return;
    }

    for (const itemName of items) {
      if (newValues[itemName]) continue;

      if (fieldTypeMap[itemName] === "Location") {
        newValues[itemName] = "2dsphere";
        continue;
      }

      newValues[itemName] = 1;
    }

    setValues({ ...newValues });
  }

  function onSelect(value: any) {
    if (
      !value?.check &&
      (arrayFieldTypes.includes(value?.fieldType) ||
        arrayFieldTypes.includes(value?.parentType))
    ) {
      setArrayFieldAPIName("");
      return;
    }

    if (
      arrayFieldTypes.includes(value?.fieldType) ||
      arrayFieldTypes.includes(value?.parentType)
    ) {
      setArrayFieldAPIName(value?.value);
    }
  }

  function switchOrder(fieldAPIName: string) {
    setValues((prev) => {
      return { ...prev, [fieldAPIName]: prev[fieldAPIName] === 1 ? -1 : 1 };
    });
  }

  function onClean() {
    setValues({});
    setArrayFieldAPIName("");
  }
};

export default CreateEditIndexDrawer;
